import '@formatjs/intl-datetimeformat/add-all-tz';
import '@formatjs/intl-datetimeformat/locale-data/en';
import '@formatjs/intl-datetimeformat/locale-data/es';
import '@formatjs/intl-datetimeformat/polyfill';
import '@formatjs/intl-numberformat/locale-data/en';
import '@formatjs/intl-numberformat/locale-data/es';
import '@formatjs/intl-numberformat/polyfill';
import '@formatjs/intl-pluralrules/locale-data/en';
import '@formatjs/intl-pluralrules/locale-data/es';
import '@formatjs/intl-pluralrules/polyfill';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import debugFn from 'debug';
import moment from 'moment';
import 'moment/locale/es';
import { PropsWithChildren, useEffect, useMemo } from 'react';
import { IntlProvider } from 'react-intl';
import { getLocaleMessages } from '../../locale/loader';
import { syncZodErrorMap } from '../../locale/zod-i18n';
import { useLocale } from '../hooks/useLocale';

const debug = debugFn('LanguageProvider');

export const DEFAULT_LOCALE = 'es';

export function LanguageProvider(p: PropsWithChildren<object>) {
  const locale = useLocale();

  const messages = useMemo(() => {
    return getLocaleMessages(locale);
  }, [locale]);

  useEffect(() => {
    debug('Set locale to %s', locale);
    moment.locale(locale);
    syncZodErrorMap(locale);
  }, [locale]);

  // If the user is in ANY Spanish variant, tell React-Intl that the
  // default text (source code) is valid for them.
  const isSourceLanguage = locale.startsWith(DEFAULT_LOCALE);

  return (
    <IntlProvider
      // key={locale}
      locale={locale}
      defaultLocale={isSourceLanguage ? locale : DEFAULT_LOCALE}
      messages={messages}
      formats={{
        date: {
          long: {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          },
          ddmm: {
            day: 'numeric',
            month: 'numeric',
          },
        },
      }}
    >
      <LocalizationProvider
        dateAdapter={AdapterLuxon}
        adapterLocale={locale.substring(0, 2).toLowerCase()}
      >
        {p.children}
      </LocalizationProvider>
    </IntlProvider>
  );
}
